// The colours of the game table (lib/tableThemes.js): the grid of the Lobby's Profil tab and of the
// waiting room, headed by the colour well in the Profil tab. Green is always free. A locked colour plays a
// rewarded video in the app (lib/ads.js); the website sends its players to the app. The hours an unlock
// lasts are not written anywhere (owner's choice). Its « new » dot is the Profil tab's (pages/Lobby.jsx).
import React, { useEffect, useState } from "react";
import { Loader2, Lock, Play } from "lucide-react";
import { toast } from "../lib/notify";
import { t, useLang } from "../lib/i18n";
import {
  TABLE_THEMES,
  isTableThemeFree,
  tableTheme,
  matStyle,
  setMyTableTheme,
  useMyTableTheme,
  useTableUnlocks,
} from "../lib/tableThemes";
import { isNativeApp, preloadRewardedVideo, rewardedVideoAvailable } from "../lib/ads";
import { unlockTableThemeWithVideo } from "../lib/tableUnlocks";

const swatchStyle = (id) => ({ ...matStyle(id), boxShadow: "inset 0 0 12px rgba(0, 0, 0, 0.55)" });

/** The six colours, 3 × 3. Picks a colour, or unlocks it with a video. */
export function TableColorGrid() {
  useLang();
  const current = useMyTableTheme();
  const unlocks = useTableUnlocks();
  const [busy, setBusy] = useState(null); // the colour whose video is playing
  const canWatch = rewardedVideoAvailable();
  const inApp = isNativeApp(); // an app without a video (no ad unit yet) must not send the player "to the app"
  const anyLocked = TABLE_THEMES.some((th) => !isTableThemeFree(th.id) && !(th.id in unlocks));

  // Load a video ahead, so a tap plays it at once.
  useEffect(() => {
    if (canWatch && anyLocked) preloadRewardedVideo().catch(() => {});
  }, [canWatch, anyLocked]);

  const pick = async (id) => {
    if (busy) return;
    if (isTableThemeFree(id) || id in unlocks) {
      setMyTableTheme(id);
      return;
    }
    if (!canWatch) {
      toast(t(inApp ? "tableColor.noVideo" : "tableColor.appOnly"));
      return;
    }
    setBusy(id);
    const result = await unlockTableThemeWithVideo(id);
    setBusy(null);
    if (result === "unlocked") {
      setMyTableTheme(id);
      toast.success(t("tableColor.unlocked", { name: t(`room.table.names.${id}`) }));
    } else if (result === "dismissed") toast(t("tableColor.dismissed"));
    else if (result === "error") toast.error(t("tableColor.saveError"));
    else toast(t("tableColor.noVideo"));
  };

  return (
    <div>
      <div className="grid grid-cols-3 gap-2">
        {TABLE_THEMES.map((th) => {
          const free = isTableThemeFree(th.id);
          const locked = !free && !(th.id in unlocks);
          const on = th.id === current;
          const name = t(`room.table.names.${th.id}`);
          return (
            <button
              key={th.id}
              type="button"
              data-testid={`table-theme-${th.id}`}
              aria-pressed={on}
              aria-label={locked ? t("tableColor.lockedLabel", { name }) : name}
              disabled={!!busy && busy !== th.id}
              onClick={() => pick(th.id)}
              className="flex flex-col items-center gap-1 min-w-0 disabled:opacity-50"
            >
              {/* The frame carries the ring: `.rami-mat` sets its own box-shadow. */}
              <span className={`relative block w-full rounded-[10px] p-[3px] ${on ? "ring-2 ring-amber-400" : "ring-1 ring-white/10"}`}>
                <span className={`rami-mat block h-9 rounded-md ${locked ? "saturate-[.6]" : ""}`} style={swatchStyle(th.id)} />
                {locked && (
                  <span className="absolute inset-[3px] flex items-center justify-center rounded-md bg-black/40">
                    {busy === th.id ? (
                      <Loader2 className="h-4 w-4 animate-spin text-amber-200" />
                    ) : (
                      // The play icon alone, no word (owner's request; screen readers get lockedLabel).
                      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-amber-200 ring-1 ring-amber-400/40">
                        {canWatch ? <Play className="h-3 w-3 translate-x-px fill-current" /> : <Lock className="h-3 w-3" />}
                      </span>
                    )}
                  </span>
                )}
              </span>
              <span className={`text-[10px] truncate max-w-full ${on ? "text-amber-300 font-semibold" : "text-slate-400"}`}>
                {name}
              </span>
            </button>
          );
        })}
      </div>
      {anyLocked && (
        <p className="mt-2 text-[10px] leading-snug text-slate-500">
          {canWatch || inApp ? t("tableColor.hintApp") : t("tableColor.hintWeb")}
        </p>
      )}
    </div>
  );
}

// The colour well, after iOS's UIColorWell: a fine ring of polished gold (the owner's choice, like
// the logo's gold; it was a ring of the table colours), a thin gap, and the felt in use as a lit disc.
// It sat on the Lobby's top-bar « Tapis » button; it now heads the « Tapis » section of the Profil tab.
// (Not `.rami-mat`: its inset shadow is sized for the whole table and turns a
// 20 px disc black — the former button could hardly be seen.)
// The light and dark stops make it read as metal; turning it moves the glints.
const WELL_RING = "conic-gradient(from 210deg, #fff1b8, #d4a72c, #8a6516, #e9c45a, #fff1b8, #b8891e, #7a5a12, #d4a72c, #fff1b8)";

export function ColorWell({ id, turned, className = "" }) {
  const th = tableTheme(id);
  return (
    <span aria-hidden="true" className={`relative block shrink-0 rounded-full ${className}`}>
      <span
        className={`absolute inset-0 rounded-full transition-transform duration-700 ease-out motion-reduce:transition-none ${
          turned ? "rotate-[150deg]" : ""
        }`}
        style={{ background: WELL_RING }}
      />
      <span className="absolute inset-[1.5px] rounded-full bg-slate-950" />
      <span
        className="absolute inset-[3.5px] rounded-full"
        style={{
          background: `radial-gradient(circle at 50% 30%, rgb(${th.glow}) 0%, rgb(${th.mid}) 62%, rgb(${th.edge}) 100%)`,
          boxShadow: "inset 0 1px 1px rgba(255, 255, 255, 0.3), inset 0 -2px 4px rgba(0, 0, 0, 0.5)",
        }}
      />
    </span>
  );
}
